import { createSelector } from 'reselect';
import _uniqBy from 'lodash/uniqBy';
import { getPartAdjustments } from './getPartAdjustments';
import { getPartAdjustmentTypes } from './getPartAdjustmentTypes';
import { getCurrentLaborTicket } from './getLaborTickets';

const getPartAdjustmentsFrom = (state) => {
  return state.partAdjustmentsFrom;
};

/**
 * The reject rows OperatorView holds for the current labor ticket, handed to the labor ticket form
 * as a fallback in case its own recorded-rejects fetch fails. The form only uses them for the
 * same ticket, when `coveredFrom` reaches back to the ticket's window, and filters them through
 * its own window — so it gets the times and counted parts, not just a total.
 *
 * Only rejects and nonconform count toward a ticket's bad parts, never part-count adjustments or
 * corrections, so rows are limited to reject reasons (scrap types) — the same rows the form
 * fetches with `scrap=true` and ERP quality reconciliation nets.
 *
 * Null when that can't be known: until part adjustments have been fetched (they're only fetched
 * with a historical parts tab, otherwise the store holds only rejects created this session), or
 * before the reject reasons are loaded.
 */
const getLaborScopeRecordedRejects = createSelector(
  [
    getPartAdjustments,
    getPartAdjustmentTypes,
    getCurrentLaborTicket,
    getPartAdjustmentsFrom,
  ],
  (
    partAdjustments,
    partAdjustmentTypes,
    currentLaborTicket,
    partAdjustmentsFrom
  ) => {
    const rejectReasonIds = new Set(
      (partAdjustmentTypes || [])
        .filter((type) => {
          return type.scrap && !type.correction;
        })
        .map((type) => {
          return type.id;
        })
    );
    if (
      !partAdjustmentsFrom ||
      !currentLaborTicket?.laborTicketRef ||
      rejectReasonIds.size === 0
    ) {
      return null;
    }

    return {
      laborTicketRef: currentLaborTicket.laborTicketRef,
      coveredFrom: partAdjustmentsFrom,
      // A poll can return a reject before its create response is appended too, so the store
      // can hold the same adjustment twice; count each once.
      adjustments: _uniqBy(partAdjustments || [], 'id')
        .filter(({ category_id: categoryId }) => {
          return rejectReasonIds.has(categoryId);
        })
        .map(({ adjustmenttime, scrapcount, nonconform_count: nc, count }) => {
          // The REST endpoint the form fetches folds nonconform into scrapcount
          // (partAdjustmentController mapAdjustments); fold here too so the
          // fallback counts nonconform rejects the same way.
          return {
            adjustmenttime,
            scrapcount: (Number(scrapcount) || 0) + (Number(nc) || 0),
            count,
          };
        }),
    };
  }
);

export { getLaborScopeRecordedRejects };
